import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import PlacePicker from '../components/wizard/PlacePicker'
import { sendPin } from '../services/trackService'

/**
 * One screen, one job: point at your house.
 *
 * It opens **inside Messenger**, in the in-app browser, tapped from a button in the chat.
 * The customer has no Machi account and has never signed in — the signed token in the URL
 * is the only thing that says which conversation this belongs to.
 *
 * It exists because the plan's first answer did not survive contact with a real phone.
 * Asking people to send a location through Messenger's own ➕ menu failed on the very first
 * test: the button hides behind a four-dot icon on some builds, below a scroll on others,
 * and does not appear at all without location permission. An instruction that does not
 * match the screen does not read as a moved button — it reads as a broken service.
 *
 * So: no navigation, no header, nothing to read. A map, a pin, one button.
 */
export default function PinLocation() {
  const [params] = useSearchParams()
  const token = params.get('t')

  /**
   * Which end of the delivery this is — wording only.
   *
   * The **authoritative** value lives inside the signed token, where whoever holds the
   * link cannot edit it. This copy of it decides nothing: at worst a wrong `k` shows the
   * wrong heading, while the pin still lands where the signature says it should.
   */
  const pickup = params.get('k') === 'pickup'

  const [loc, setLoc] = useState({ query: '', lat: null, lng: null, label: '' })
  const [sending, setSending] = useState(false)
  const [done, setDone] = useState(false)
  const [failed, setFailed] = useState(null)

  const placed = loc.lat != null && loc.lng != null

  const confirm = async () => {
    setSending(true)
    setFailed(null)

    try {
      await sendPin({ token, latitude: loc.lat, longitude: loc.lng, address: loc.label })
      setDone(true)
    } catch (error) {
      setFailed(error)
    } finally {
      setSending(false)
    }
  }

  if (!token) {
    return (
      <Shell>
        <p className="text-body font-semibold text-ink">Kulang po ang link</p>
        <p className="mt-2 text-body-s text-ink-3">
          Bumalik po kayo sa chat at pindutin ulit ang <strong>Ituro sa mapa</strong>.
        </p>
      </Shell>
    )
  }

  /**
   * The end of the road, and it says so.
   *
   * Nothing here can close the in-app browser for them — the button that does is
   * Messenger's own. So the copy points at it rather than pretending the page can.
   */
  if (done) {
    return (
      <Shell>
        <p className="text-[2rem] leading-none">✅</p>
        <p className="mt-3 text-body font-semibold text-ink">Nakuha na po namin!</p>
        <p className="mt-2 text-body-s text-ink-3">
          Pwede niyo na pong isara ito at bumalik sa chat — nandoon na po ang susunod.
        </p>
      </Shell>
    )
  }

  return (
    <main className="min-h-dvh bg-surface px-5 py-6">
      <div className="mx-auto flex w-full max-w-[26rem] flex-col gap-4">
        <div>
          <p className="text-label font-semibold uppercase tracking-wide text-ink-3">Machi Express</p>
          <h1 className="mt-1 text-[1.375rem] font-bold leading-tight text-ink">
            {pickup ? 'Saan po kukunin?' : 'Saan po ihahatid?'}
          </h1>
          <p className="mt-1.5 text-body-s text-ink-3">
            {pickup
              ? 'Ituro po ang tindahan o lugar na pupuntahan ng rider.'
              : <>Hanapin po ang lugar, o pindutin ang <strong>Kasalukuyang lokasyon</strong>.</>}
          </p>
        </div>

        {/* The same picker the order form uses — map, Google Places, Nominatim fallback.
            Reused rather than rebuilt: a second address picker is a second place for the
            pin to be subtly wrong. */}
        <PlacePicker kind={pickup ? 'pickup' : 'dropoff'} loc={loc} setLoc={setLoc} />

        {failed && (
          <p role="alert" className="text-body-s text-alert">
            {failed.status === 401
              ? 'Nag-expire na po ang link. Bumalik po sa chat at pindutin ulit ang Ituro sa mapa.'
              : 'Hindi po naipadala. Pakisubukan ulit.'}
          </p>
        )}

        {/* Disabled until a pin exists. A confirm button that can be pressed with nothing
            selected is a button that sends nothing and looks broken. */}
        <button
          type="button"
          disabled={!placed || sending}
          onClick={confirm}
          className="w-full rounded-card bg-brand px-4 py-3.5 text-body font-semibold text-white
                     disabled:bg-surface-2 disabled:text-ink-3"
        >
          {sending ? 'Ipinapadala…' : placed ? 'Ito na po' : 'Ituro muna ang lugar'}
        </button>
      </div>
    </main>
  )
}

const Shell = ({ children }) => (
  <main className="min-h-dvh bg-surface-2 px-5 py-8">
    <div className="mx-auto w-full max-w-[26rem] rounded-card border border-hairline bg-surface p-6 text-center">
      {children}
    </div>
  </main>
)
